/* frontend/src/pages/CatalogoPage.js */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Search, Filter, ChevronDown, ChevronUp, Car, Calendar, GaugeCircle, Fuel, Repeat } from 'lucide-react';
import api from '../api';

// Shadcn UI components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Typography } from '../components/ui/typography';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Pagination } from '../components/Pagination';

export default function CatalogoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [marcaFilter, setMarcaFilter] = useState('');
  const [anoFilter, setAnoFilter] = useState('');
  const [valorMinFilter, setValorMinFilter] = useState('');
  const [valorMaxFilter, setValorMaxFilter] = useState('');
  const [combustivelFilter, setCombustivelFilter] = useState('');
  const [cambioFilter, setCambioFilter] = useState('');
  const [kmMinFilter, setKmMinFilter] = useState('');
  const [kmMaxFilter, setKmMaxFilter] = useState('');
  const [sortBy, setSortBy] = useState('relevancia');
  
  // Opções de filtro disponíveis
  const [marcas, setMarcas] = useState([]);
  const [anos, setAnos] = useState([]);
  const [combustiveis, setCombustiveis] = useState([]);
  const [cambios, setCambios] = useState([]);

  // Carrega os veículos disponíveis
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/public/catalogo?page=${currentPage}&limit=20`);
        const { vehicles: vehiclesList, pagination: paginationData } = response.data;
        setVehicles(vehiclesList);
        setFilteredVehicles(vehiclesList);
        setPagination(paginationData);
        
        // Extrai marcas únicas para filtro
        const uniqueMarcas = [...new Set(vehiclesList
          .filter(v => v.infoTecnica?.marca)
          .map(v => v.infoTecnica.marca))].sort();
        setMarcas(uniqueMarcas);
        
        // Extrai anos únicos para filtro
        const uniqueAnos = [...new Set(vehiclesList
          .filter(v => v.infoTecnica?.anoFabricacao)
          .map(v => v.infoTecnica.anoFabricacao))].sort((a, b) => b - a);
        setAnos(uniqueAnos);

        // Extrai combustíveis únicos
        const uniqueCombustiveis = [...new Set(vehiclesList
          .filter(v => v.infoTecnica?.combustivel)
          .map(v => v.infoTecnica.combustivel))].sort();
        setCombustiveis(uniqueCombustiveis);

        // Extrai tipos de câmbio únicos
        const uniqueCambios = [...new Set(vehiclesList
          .filter(v => v.infoTecnica?.cambio)
          .map(v => v.infoTecnica.cambio))].sort();
        setCambios(uniqueCambios);
      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
        setError(t('catalogo.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    loadVehicles();
  }, [currentPage]);
  
  // Aplica filtros quando qualquer filtro mudar
  useEffect(() => {
    let result = [...vehicles];
    
    // Filtrar por termo de busca (modelo, marca ou versão)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(v => 
        v.infoTecnica?.marca?.toLowerCase().includes(term) || 
        v.infoTecnica?.modelo?.toLowerCase().includes(term) ||
        v.infoTecnica?.versao?.toLowerCase().includes(term)
      );
    }
    
    // Filtrar por marca
    if (marcaFilter) {
      result = result.filter(v => v.infoTecnica?.marca === marcaFilter);
    }
    
    // Filtrar por ano
    if (anoFilter) {
      result = result.filter(v => v.infoTecnica?.anoFabricacao === parseInt(anoFilter));
    }
    
    // Filtrar por valor mínimo
    if (valorMinFilter) {
      result = result.filter(v => {
        const valor = v.comercial?.valorVenda || 0;
        return valor >= parseFloat(valorMinFilter);
      });
    }
    
    // Filtrar por valor máximo
    if (valorMaxFilter) {
      result = result.filter(v => {
        const valor = v.comercial?.valorVenda || 0;
        return valor <= parseFloat(valorMaxFilter);
      });
    }

    // Filtrar por combustível
    if (combustivelFilter) {
      result = result.filter(v => v.infoTecnica?.combustivel === combustivelFilter);
    }

    // Filtrar por câmbio
    if (cambioFilter) {
      result = result.filter(v => v.infoTecnica?.cambio === cambioFilter);
    }

    // Filtrar por km mínimo
    if (kmMinFilter) {
      result = result.filter(v => {
        const km = v.infoTecnica?.km || 0;
        return km >= parseFloat(kmMinFilter);
      });
    }

    // Filtrar por km máximo
    if (kmMaxFilter) {
      result = result.filter(v => {
        const km = v.infoTecnica?.km || 0;
        return km <= parseFloat(kmMaxFilter);
      });
    }

    // Ordenação
    if (sortBy === 'maior_preco') {
      result.sort((a, b) => (b.comercial?.valorVenda || 0) - (a.comercial?.valorVenda || 0));
    } else if (sortBy === 'menor_preco') {
      result.sort((a, b) => (a.comercial?.valorVenda || 0) - (b.comercial?.valorVenda || 0));
    } else if (sortBy === 'mais_recente') {
      result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === 'menor_km') {
      result.sort((a, b) => (a.infoTecnica?.km || 0) - (b.infoTecnica?.km || 0));
    }
    
    setFilteredVehicles(result);
    
    // Resetar para primeira página quando aplicar filtros
    if (searchTerm || marcaFilter || anoFilter || valorMinFilter || valorMaxFilter || 
        combustivelFilter || cambioFilter || kmMinFilter || kmMaxFilter) {
      setCurrentPage(1);
    }
  }, [vehicles, searchTerm, marcaFilter, anoFilter, valorMinFilter, valorMaxFilter, combustivelFilter, cambioFilter, kmMinFilter, kmMaxFilter, sortBy]);

  // Formatar valor para exibição
  const formatCurrency = (value) => {
    if (!value) return t('catalogo.pricing.onRequest');
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Monta URL absoluta para fotos
  const getUrl = (src) => {
    if (!src) return null;
    return src.startsWith('http')
      ? src
      : `${api.defaults.baseURL.replace('/api', '')}${src}`;
  };

  // Função para limpar todos os filtros
  const clearFilters = () => {
    setSearchTerm('');
    setMarcaFilter('');
    setAnoFilter('');
    setValorMinFilter('');
    setValorMaxFilter('');
    setCombustivelFilter('');
    setCambioFilter('');
    setKmMinFilter('');
    setKmMaxFilter('');
    setSortBy('relevancia');
  };

  // Conta quantos filtros estão ativos
  const countActiveFilters = () => {
    let count = 0;
    if (marcaFilter) count++;
    if (anoFilter) count++;
    if (valorMinFilter || valorMaxFilter) count++;
    if (combustivelFilter) count++;
    if (cambioFilter) count++;
    if (kmMinFilter || kmMaxFilter) count++;
    return count;
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{t('catalogo.meta.title')}</title>
          <meta name="description" content={t('catalogo.meta.description')} />
          <meta name="keywords" content={t('catalogo.meta.keywords')} />
        </Helmet>
        <div className="container mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Spinner className="h-10 w-10 text-primary" />
            <p className="text-muted-foreground">{t('catalogo.loading.message')}</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{t('catalogo.meta.title')}</title>
          <meta name="description" content={t('catalogo.meta.description')} />
          <meta name="keywords" content={t('catalogo.meta.keywords')} />
        </Helmet>
        <div className="container mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
          <div className="max-w-md text-center">
            <Car className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <Typography variant="h3" className="mb-2">{t('catalogo.errors.title')}</Typography>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => window.location.reload()}>
              {t('catalogo.errors.tryAgain')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Principal seção do catálogo
  return (
    <>
      <style jsx>{`
        .catalogo-banner h1 {
          color: white !important;
        }
      `}</style>
      <Helmet>
        <title>{t('catalogo.meta.title')}</title>
        <meta name="description" content={t('catalogo.meta.fullDescription')} />
        <meta name="keywords" content={t('catalogo.meta.keywords')} />
        <meta property="og:title" content={t('catalogo.meta.ogTitle')} />
        <meta property="og:description" content={t('catalogo.meta.ogDescription')} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/catalogo" />
      </Helmet>
      <div className="flex flex-col">
        {/* Banner/Header da página */}
        <div className="bg-gradient-to-r from-primary/90 to-primary text-white py-16 md:py-24 catalogo-banner">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {t('catalogo.banner.title')}
              </h1>
              <Typography variant="p" className="text-lg md:text-xl opacity-90 mb-8">
                {t('catalogo.banner.subtitle')}
              </Typography>
              
              {/* Barra de busca principal */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                <Input
                  type="text"
                  placeholder={t('catalogo.search.placeholder')}
                  className="pl-10 bg-white dark:bg-gray-800 border-0 h-12 text-black dark:text-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Seção principal com filtros e listagem */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Filtros (coluna lateral) */}
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="bg-card border rounded-lg p-4 sticky top-20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Typography variant="h4" className="font-medium">{t('catalogo.filters.title')}</Typography>
                    {countActiveFilters() > 0 && (
                      <Badge variant="secondary">{countActiveFilters()}</Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs h-8">
                    {t('catalogo.filters.clearAll')}
                  </Button>
                </div>
                
                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="marca" defaultOpen>
                    <AccordionTrigger>{t('catalogo.filters.brand.title')}</AccordionTrigger>
                    <AccordionContent>
                      <Select value={marcaFilter || "all"} onValueChange={(value) => setMarcaFilter(value === "all" ? "" : value)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('catalogo.filters.brand.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('catalogo.filters.brand.all')}</SelectItem>
                          {marcas.map(marca => (
                            <SelectItem key={marca} value={marca}>{marca}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="ano">
                    <AccordionTrigger>{t('catalogo.filters.year.title')}</AccordionTrigger>
                    <AccordionContent>
                      <Select value={anoFilter || "all"} onValueChange={(value) => setAnoFilter(value === "all" ? "" : value)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('catalogo.filters.year.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('catalogo.filters.year.all')}</SelectItem>
                          {anos.map(ano => (
                            <SelectItem key={ano} value={ano.toString()}>{ano}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="valor">
                    <AccordionTrigger>{t('catalogo.filters.priceRange.title')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-muted-foreground block mb-1">
                            {t('catalogo.filters.priceRange.from')}:
                          </label>
                          <Input
                            type="number"
                            placeholder={t('catalogo.filters.priceRange.minPlaceholder')}
                            value={valorMinFilter}
                            onChange={(e) => setValorMinFilter(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground block mb-1">
                            {t('catalogo.filters.priceRange.to')}:
                          </label>
                          <Input
                            type="number"
                            placeholder={t('catalogo.filters.priceRange.maxPlaceholder')}
                            value={valorMaxFilter}
                            onChange={(e) => setValorMaxFilter(e.target.value)}
                          />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="combustivel">
                    <AccordionTrigger>{t('catalogo.filters.fuel.title')}</AccordionTrigger>
                    <AccordionContent>
                      <Select value={combustivelFilter || "all"} onValueChange={(value) => setCombustivelFilter(value === "all" ? "" : value)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('catalogo.filters.fuel.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('catalogo.filters.fuel.all')}</SelectItem>
                          {combustiveis.map(combustivel => (
                            <SelectItem key={combustivel} value={combustivel}>{combustivel}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="cambio">
                    <AccordionTrigger>{t('catalogo.filters.transmission.title')}</AccordionTrigger>
                    <AccordionContent>
                      <Select value={cambioFilter || "all"} onValueChange={(value) => setCambioFilter(value === "all" ? "" : value)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('catalogo.filters.transmission.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('catalogo.filters.transmission.all')}</SelectItem>
                          {cambios.map(cambio => (
                            <SelectItem key={cambio} value={cambio}>{cambio}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="quilometragem">
                    <AccordionTrigger>{t('catalogo.filters.mileage.title')}</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-muted-foreground block mb-1">
                            {t('catalogo.filters.mileage.from')}:
                          </label>
                          <Input
                            type="number"
                            placeholder={t('catalogo.filters.mileage.minPlaceholder')}
                            value={kmMinFilter}
                            onChange={(e) => setKmMinFilter(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground block mb-1">
                            {t('catalogo.filters.mileage.to')}:
                          </label>
                          <Input
                            type="number"
                            placeholder={t('catalogo.filters.mileage.maxPlaceholder')}
                            value={kmMaxFilter}
                            onChange={(e) => setKmMaxFilter(e.target.value)}
                          />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
            
            {/* Listagem de veículos (grid) */}
            <div className="flex-1">
              {/* Status da pesquisa e ordenação */}
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-muted-foreground">
                  {filteredVehicles.length} {filteredVehicles.length === 1 ? t('catalogo.results.vehicleFound') : t('catalogo.results.vehiclesFound')}
                </p>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('catalogo.sorting.label')}:</span>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder={t('catalogo.sorting.relevance')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relevancia">{t('catalogo.sorting.relevance')}</SelectItem>
                      <SelectItem value="menor_preco">{t('catalogo.sorting.lowestPrice')}</SelectItem>
                      <SelectItem value="maior_preco">{t('catalogo.sorting.highestPrice')}</SelectItem>
                      <SelectItem value="mais_recente">{t('catalogo.sorting.newest')}</SelectItem>
                      <SelectItem value="menor_km">{t('catalogo.sorting.lowestMileage')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Exibição dos filtros ativos */}
              {countActiveFilters() > 0 && (
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {marcaFilter && (
                      <Badge variant="secondary" className="flex gap-2 items-center">
                        {t('catalogo.activeFilters.brand')}: {marcaFilter}
                        <button className="text-xs" onClick={() => setMarcaFilter('')}>✕</button>
                      </Badge>
                    )}
                    {anoFilter && (
                      <Badge variant="secondary" className="flex gap-2 items-center">
                        {t('catalogo.activeFilters.year')}: {anoFilter}
                        <button className="text-xs" onClick={() => setAnoFilter('')}>✕</button>
                      </Badge>
                    )}
                    {(valorMinFilter || valorMaxFilter) && (
                      <Badge variant="secondary" className="flex gap-2 items-center">
                        {t('catalogo.activeFilters.value')}: {valorMinFilter ? `R$${valorMinFilter}` : 'R$0'} - {valorMaxFilter ? `R$${valorMaxFilter}` : t('catalogo.activeFilters.noLimit')}
                        <button className="text-xs" onClick={() => { setValorMinFilter(''); setValorMaxFilter(''); }}>✕</button>
                      </Badge>
                    )}
                    {combustivelFilter && (
                      <Badge variant="secondary" className="flex gap-2 items-center">
                        {t('catalogo.activeFilters.fuel')}: {combustivelFilter}
                        <button className="text-xs" onClick={() => setCombustivelFilter('')}>✕</button>
                      </Badge>
                    )}
                    {cambioFilter && (
                      <Badge variant="secondary" className="flex gap-2 items-center">
                        {t('catalogo.activeFilters.transmission')}: {cambioFilter}
                        <button className="text-xs" onClick={() => setCambioFilter('')}>✕</button>
                      </Badge>
                    )}
                    {(kmMinFilter || kmMaxFilter) && (
                      <Badge variant="secondary" className="flex gap-2 items-center">
                        {t('catalogo.activeFilters.mileage')}: {kmMinFilter || '0'} - {kmMaxFilter || t('catalogo.activeFilters.noLimit')}
                        <button className="text-xs" onClick={() => { setKmMinFilter(''); setKmMaxFilter(''); }}>✕</button>
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              {filteredVehicles.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVehicles.map(vehicle => (
                      <Card key={vehicle._id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => navigate(`/catalogo/${vehicle._id}`)}>
                        <div className="relative aspect-[16/9] bg-muted">
                          {vehicle.midia?.fotos && vehicle.midia.fotos.length > 0 ? (
                            <img
                              src={getUrl(vehicle.midia.fotos[0])}
                              alt={`${vehicle.infoTecnica?.marca} ${vehicle.infoTecnica?.modelo}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = '/placeholder-car.jpg';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Car className="h-12 w-12 text-muted-foreground" />
                            </div>
                          )}
                          
                          {/* Badges para destaque */}
                          {vehicle.infoTecnica?.km < 10000 && (
                            <Badge className="absolute top-2 left-2 bg-green-600">{t('catalogo.badges.lowMileage')}</Badge>
                          )}
                          {vehicle.infoTecnica?.anoFabricacao >= new Date().getFullYear() - 1 && (
                            <Badge className="absolute top-2 right-2 bg-blue-600">{t('catalogo.badges.brandNew')}</Badge>
                          )}
                        </div>
                        
                        <CardContent className="p-4">
                          <div className="mb-2 flex items-center justify-between">
                            <Typography variant="h4" className="font-bold truncate">
                              {vehicle.infoTecnica?.modelo || t('catalogo.vehicleCard.defaultTitle')}
                            </Typography>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-3 truncate">
                            {vehicle.infoTecnica?.marca} {vehicle.infoTecnica?.versao}
                          </p>
                          
                          <Typography variant="h4" className="text-primary font-bold mb-3">
                            {formatCurrency(vehicle.comercial?.valorVenda)}
                          </Typography>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>{vehicle.infoTecnica?.anoFabricacao}/{vehicle.infoTecnica?.anoModelo}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <GaugeCircle className="h-3 w-3" />
                              <span>{vehicle.infoTecnica?.km?.toLocaleString('pt-BR') || '0'} km</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Fuel className="h-3 w-3" />
                              <span>{vehicle.infoTecnica?.combustivel || t('catalogo.vehicleCard.notAvailable')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Repeat className="h-3 w-3" />
                              <span>{vehicle.infoTecnica?.cambio || t('catalogo.vehicleCard.notAvailable')}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Paginação */}
                  <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t('catalogo.pagination.showing', {
                        start: pagination.totalItems === 0 ? 0 : (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                        end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
                        total: pagination.totalItems
                      })}
                    </div>
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Car className="h-12 w-12 text-muted-foreground mb-4" />
                  <Typography variant="h4" className="mb-2">{t('catalogo.emptyState.title')}</Typography>
                  <p className="text-muted-foreground mb-6">
                    {t('catalogo.emptyState.description')}
                  </p>
                  <Button onClick={clearFilters}>{t('catalogo.emptyState.clearFilters')}</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
